import { AlertAddToListButton } from '@/components/AlertAddToListButton';
import { OutcomeBadge } from '@/components/DataTable/alerts/CellRenderers';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { ScanDetailFormattedText } from '@/components/ScanDetailFormattedText';
import { Badge } from '@/components/ui/badge';
import type { AddFromAlertScope } from '@/hooks/api/useAddPolicyExceptionFromAlert';
import { cn } from '@/lib/utils';
import { hasKnownEntityName } from '@kirin/api-client/helpers/source-details';
import { AlertOsvComponentSection } from './AlertOsvComponentSection';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { AlertDescription } from './AlertDescription';
import { AlertSourceDetails } from './AlertSourceDetails';
import { AlertSecretDetails } from './AlertSecretDetails';
import { AlertSecurityGroupDetail } from './AlertSecurityGroupDetail';
import { AlertEvidenceExcerpt } from './AlertEvidenceExcerpt';
import { stripMcpToolResponseLocationPrefix } from './alert-location';
import { hasSecretDetails } from './AlertSecretDetails.helpers';
import { AggregateScoreChip } from './AggregateScoreChip';
import { SecretConfidenceBadge } from './SecretConfidenceBadge';
import { DetectionLogic } from './DetectionLogic';
import { RecommendedActions } from './RecommendedActions';
import { WhyThisFired } from './WhyThisFired';
import {
  EVIDENCE_NOT_APPLICABLE_COPY,
  isEvidenceNotApplicable,
} from './evidence-state';
import {
  useAlertDetailModel,
  type AlertDetailModelInput,
} from './useAlertDetailModel';

interface AlertDetailCardProps {
  alert: AlertDetailModelInput;
  className?: string;
  /** Org scope for scan-result links; falls back to the route param when omitted. */
  organizationId?: string;
  /**
   * When provided (together with {@link alertKey}), the card header surfaces the
   * "Add to Allow/Block List" action (KIR-3558) for the alert's entity, placed
   * top-right next to the severity badge. Omitted where the caller can't resolve
   * a scope, in which case no action is shown.
   */
  scope?: AddFromAlertScope;
  /** Alert key, threaded into the add-to-list action. */
  alertKey?: string;
  /**
   * KIR-4281 — `'occurrence'` renders this card for ONE occurrence. The legacy
   * card has no actions footer or facts strip to suppress, but it does render
   * {@link AlertSecretDetails}, whose representative-value hints are wrong for
   * a single occurrence — so the variant is threaded through for those.
   */
  variant?: 'group' | 'occurrence';
}

/**
 * Renders alert type, severity, description, and optional location in a bordered card.
 * Shared between the AlertsTable expanded row and the AlertOccurrencesPanel modal layout.
 *
 * KIR-2381: revamp on -> centralized SUB_POLICY_DISPLAY label + description;
 * off -> legacy `alert.type` / `alert.description` for byte-identical off-state.
 *
 * KIR-4481: the per-alert derivation lives in {@link useAlertDetailModel}, shared
 * with the redesigned `AlertDetailCards`, so the two layouts cannot drift.
 */
export function AlertDetailCard({
  alert,
  className,
  organizationId: organizationIdProp,
  scope,
  alertKey,
  variant = 'group',
}: AlertDetailCardProps): ReactElement {
  const {
    organizationId,
    sourceDetailsEnabled,
    capabilityDetectionEnabled,
    secretDetailsEnabled,
    detectionLogicEnabled,
    gitleaksRulesPath,
    osvComponent,
    osvAdvisoryId,
    secretDetails,
    label,
    description,
    cardSections,
    mitigation,
    surroundingText,
    isExtensionSecurityAlert,
    mcpLocation,
    locationForDisplay,
    scanResultLink,
    chatExcerpt,
    commandExcerpt,
    toolCallExcerpt,
    suppressRedundantLocation,
  } = useAlertDetailModel(alert, organizationIdProp, variant);
  // KIR-4269: a coverage-gap finding (an incomplete scan) has no detected
  // content — show the honest not-applicable evidence state, and suppress
  // Detection Logic so the card does not present a reason for a non-finding.
  const evidenceNotApplicable = isEvidenceNotApplicable(alert.type);
  return (
    <div
      className={cn(
        'rounded-lg border bg-white p-3 dark:bg-slate-800',
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {capabilityDetectionEnabled && isExtensionSecurityAlert ? (
          <Badge variant="outline">{label}</Badge>
        ) : (
          <span className="font-medium text-sm">{label}</span>
        )}
        <SeverityBadge riskLevel={alert.severity} showIcon />
        {capabilityDetectionEnabled && isExtensionSecurityAlert && (
          <>
            <OutcomeBadge outcome={alert.outcome} />
            <AggregateScoreChip score={alert.scoreBreakdown?.score} />
          </>
        )}
        {secretDetailsEnabled && typeof alert.entropy === 'number' && (
          <SecretConfidenceBadge entropy={alert.entropy} />
        )}
        {scope && alertKey && (
          <AlertAddToListButton
            scope={scope}
            sourceDetails={alert.sourceDetails}
            alertKey={alertKey}
            analyticsContext={{ severity: alert.severity, type: alert.type }}
            className="ml-32 shrink-0"
          />
        )}
      </div>
      <AlertDescription
        sections={cardSections}
        fallbackDescription={description}
        {...(surroundingText ? { surroundingText } : {})}
        className="text-sm text-muted-foreground mb-1"
      />
      {/*
        KIR-4272: Detection Logic sits directly under "Why it matters" and above
        the Location block — the card reads type-level context first ("what
        happened", "why it matters"), then why THIS finding fired, before the
        per-occurrence metadata below. The legacy "Why this fired" section keeps
        its original position further down, so a flag-off card is unchanged in
        both content and layout.
      */}
      {/* KIR-4269: honest not-applicable evidence state for coverage-gap findings,
          reading distinctly from an "unavailable" reason. */}
      {evidenceNotApplicable && (
        <p
          className="mt-3 mb-1 text-sm text-muted-foreground"
          data-testid="evidence-not-applicable"
        >
          {EVIDENCE_NOT_APPLICABLE_COPY}
        </p>
      )}
      {detectionLogicEnabled && !evidenceNotApplicable && (
        <DetectionLogic
          alert={alert}
          sourceDetailsAvailable={sourceDetailsEnabled}
          secretDetailsAvailable={secretDetailsEnabled}
          {...(gitleaksRulesPath !== undefined ? { gitleaksRulesPath } : {})}
          className="mb-1"
        />
      )}
      {/*
        KIR-4212: the actual chat content the classifier flagged (code / shell
        commands / tool calls), masked at ingest. Sits under Detection Logic —
        the card reads reasoning + factors, then the evidence behind them. Reuses
        the secret / PII redacted-sample `<pre>` styling so every masked excerpt
        panel looks identical; the snippet is already secret- and PII-screened by
        the backend, rendered verbatim.
      */}
      {chatExcerpt && (
        <div className="mt-2 flex flex-col gap-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Chat excerpt
          </p>
          <pre className="max-h-72 overflow-auto rounded bg-slate-100 p-2 font-mono text-xs break-words whitespace-pre-wrap dark:bg-slate-700">
            {chatExcerpt}
          </pre>
        </div>
      )}
      {/*
        KIR-4213: a window of the scanned command file around the flagged
        command, masked at ingest. `location` only shows a per-factor-truncated
        teaser; this block renders the whole stored excerpt (a bounded window
        built by the backend) so the flagged command is never hidden.
      */}
      {commandExcerpt && (
        <AlertEvidenceExcerpt
          label="Command excerpt"
          content={commandExcerpt}
        />
      )}
      {toolCallExcerpt && (
        <AlertEvidenceExcerpt
          label="Tool-call excerpt"
          content={toolCallExcerpt}
        />
      )}
      {/*
        KIR-4272 drops KIR-3800's in-card "Security Group" row: the group is
        already a sortable, filterable column on the alerts table, and the
        expanded card exists for detail the row cannot show. `securityGroups`
        still feeds that column (`AlertSecurityGroupsCell`) and the occurrences
        panel, both of which render deleted groups as "<name> (deleted)" via the
        same `securityGroupLabel` helper — so no attribution is lost, only the
        duplicate.

        Gated on the flag rather than deleted outright. KIR-3800 AC #13/#14
        shipped this row, so removing it unconditionally would regress a live,
        already-accepted requirement for every org that has not enabled
        `alert_detection_logic` — and would contradict the flag-off parity the
        comment above promises. Flag off: the pre-KIR-4272 card, unchanged.
      */}
      {!detectionLogicEnabled && (
        <AlertSecurityGroupDetail securityGroups={alert.securityGroups} />
      )}
      {(mcpLocation || locationForDisplay) && !suppressRedundantLocation && (
        <div className="mt-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Location
          </p>
          <div className="mt-1 text-xs font-mono bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded min-w-0 max-w-full">
            {mcpLocation ? (
              <>
                {mcpLocation.identity && <div>{mcpLocation.identity}</div>}
                <div className="mt-2 text-muted-foreground">
                  Tool description:
                </div>
                <div className="break-words whitespace-pre-wrap">
                  {mcpLocation.before}
                  <mark className="rounded bg-amber-100 px-1 py-0.5 font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                    {mcpLocation.match}
                  </mark>
                  {mcpLocation.after}
                </div>
              </>
            ) : (
              // KIR-4100: strip the `mcp-tool-response:` namespace for display.
              // KIR-4213: `break-all` (not `break-words`) so a long file path —
              // no spaces to wrap on — shows in FULL instead of being clipped;
              // scoped to this branch so it never breaks the MCP identity line
              // above mid-word.
              <div className="break-all">
                <ScanDetailFormattedText
                  location={
                    stripMcpToolResponseLocationPrefix(locationForDisplay) ?? ''
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}
      {/*
        KIR-4272: Detection Logic (rendered above, under "Why it matters")
        supersedes "Why this fired" (KIR-3781) — it answers the same question for
        every alert type, not just weighted extension findings. Exactly one of
        the two renders: `!detectionLogicEnabled` is what makes them mutually
        exclusive, so the legacy section can stay in its original slot until the
        flag is fully rolled out and a flag-off card is byte- AND layout-identical
        to the pre-feature one.

        It also keeps its original `capability_detection` + extension-alert gate
        verbatim. `scoreBreakdown` is on the DTO regardless of that flag, so
        dropping the gate would newly show the weight breakdown to orgs that
        never enabled capability detection — the opposite of the flag-off parity
        this fallback exists to preserve.
      */}
      {!detectionLogicEnabled &&
        capabilityDetectionEnabled &&
        isExtensionSecurityAlert && (
          <WhyThisFired
            type={alert.type}
            scoreBreakdown={alert.scoreBreakdown}
          />
        )}
      {/*
        KIR-4190: a sibling of AlertSourceDetails, not a child, on purpose —
        that panel is also gated on `hasKnownEntityName`, and an MCP dependency
        finding with no parseable config has an Unknown Name, which would hide
        the CVE/CVSS too. Same `alert_source_details` flag either way.
      */}
      {sourceDetailsEnabled && osvComponent && (
        <AlertOsvComponentSection
          osvComponent={osvComponent}
          {...(osvAdvisoryId ? { osvId: osvAdvisoryId } : {})}
        />
      )}
      {alert.sourceDetails?.kind === 'pii' ? (
        // PII redacted-content sample (KIR-3034): shown whenever present — it
        // carries no Allow/Block entity, so the entity-name gate doesn't apply.
        <AlertSourceDetails sourceDetails={alert.sourceDetails} />
      ) : (
        sourceDetailsEnabled &&
        alert.sourceDetails &&
        hasKnownEntityName(alert.sourceDetails) && (
          <AlertSourceDetails sourceDetails={alert.sourceDetails} />
        )
      )}
      {secretDetailsEnabled && hasSecretDetails(secretDetails) && (
        <AlertSecretDetails
          details={secretDetails}
          organizationId={organizationId}
          variant={variant}
        />
      )}
      {/*
        KIR-4271: Recommended actions — last content block, above the
        scan-result link, so the guidance reads as separate from the
        per-occurrence metadata above it and from the navigation below (the
        same position the grouped-cards layout gives it relative to its actions
        footer). `mitigation` arrives already gated on
        `alert_recommended_actions` + `alerts_revamp` and null when the mapping
        has no approved copy, so the section is simply absent then.
      */}
      {mitigation != null && (
        <RecommendedActions
          mitigation={mitigation}
          heading="Recommended actions"
          className="mt-3"
        />
      )}
      {scanResultLink && (
        <Link
          to={scanResultLink}
          className="mt-3 inline-flex text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          View full scan result →
        </Link>
      )}
    </div>
  );
}

AlertDetailCard.displayName = 'AlertDetailCard';
